import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Clock, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { QUESTIONS, FEEDBACK_PAIRS, QUIZ_TITLE, QUIZ_SUBTITLE } from "@/data/funnel";
import { submitLead, getOffers } from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import Interstitial from "@/components/Interstitial";
import OfferResult from "@/components/OfferResult";
import LeadForm from "@/components/LeadForm";
import ThankYou from "@/components/ThankYou";

// Linear flow: lead form -> Q1 -> feedback -> Q2(budget) -> feedback -> offer
const STEPS = [
  { type: "leadform" },
  { type: "question", q: 0 },
  { type: "interstitial", pair: 0 },
  { type: "question", q: 1 },
  { type: "interstitial", pair: 1 },
  { type: "result" },
];

const TOTAL_STEPS = STEPS.length;

export default function QuizFunnel() {
  const [phase, setPhase] = useState("intro"); // intro | flow | thankyou
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [budgetKey, setBudgetKey] = useState(null);
  const [offers, setOffers] = useState(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    getOffers().then(setOffers).catch(() => {});
  }, []);

  const tier =
    offers && budgetKey ? offers.tiers[offers.budget_map[budgetKey]] : null;

  const current = STEPS[stepIndex];

  const progress =
    phase === "intro"
      ? 0
      : phase === "thankyou"
      ? 100
      : Math.round(((stepIndex + 1) / TOTAL_STEPS) * 100);

  const advance = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, TOTAL_STEPS - 1));
  }, []);

  const handleFormContinue = (data) => {
    setForm(data);
    advance();
  };

  const handleSelect = (option) => {
    const q = QUESTIONS[current.q];
    setAnswers((prev) => [
      ...prev.filter((a) => a.qid !== q.id),
      { qid: q.id, question: q.text, answer: option.label },
    ]);
    if (q.isBudget) setBudgetKey(option.key);
    advance();
  };

  // Auto-submit the lead once all data is collected (when reaching the offer)
  useEffect(() => {
    if (phase !== "flow" || current.type !== "result") return;
    if (submittedRef.current || !form || !budgetKey) return;
    submittedRef.current = true;
    submitLead({
      name: form.name,
      phone: form.phone,
      email: form.email,
      budget_key: budgetKey,
      answers: answers.map((a) => ({ question: a.question, answer: a.answer })),
    })
      .then(() => {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "Lead", { content_name: budgetKey });
        }
      })
      .catch(() => {
        toast.error("Non siamo riusciti a inviare i dati, riprova più tardi.");
      });
  }, [phase, current, form, budgetKey, answers]);

  const handleReset = () => {
    submittedRef.current = false;
    setForm(null);
    setAnswers([]);
    setBudgetKey(null);
    setStepIndex(0);
    setPhase("intro");
  };

  const showRestart = phase === "flow";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-30 bg-[#050505] overflow-y-auto"
      data-testid="quiz-view"
    >
      {/* Progress + top bar */}
      <div className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/10">
        <div className="h-1 w-full bg-white/5">
          <motion.div
            className="h-full bg-[#8b5cf6]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <div className="flex items-center justify-between px-6 md:px-12 py-4">
          <div className="leading-tight" data-testid="brand-badge">
            <span className="block font-display font-extrabold text-white text-sm sm:text-base">
              MIRLO'S COMPANY
            </span>
            <span className="block text-[9px] sm:text-[10px] font-bold tracking-[0.12em] uppercase text-[#8b5cf6]">
              Nella Top 10 realtà formative in Italia 2026
            </span>
          </div>
          {showRestart && (
            <button
              onClick={handleReset}
              data-testid="quiz-restart-btn"
              className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Ricomincia <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="min-h-[calc(100vh-57px)] flex items-center justify-center px-6 py-16">
        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
              className="w-full max-w-2xl mx-auto text-center"
              data-testid="quiz-intro"
            >
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#8b5cf6]">
                Questionario di profilazione
              </span>
              <h1 className="mt-5 font-montserrat font-medium tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.08] text-white">
                {QUIZ_TITLE.charAt(0).toUpperCase() + QUIZ_TITLE.slice(1)}
              </h1>
              <p className="mt-5 text-lg text-zinc-400">
                {QUIZ_SUBTITLE.charAt(0).toUpperCase() + QUIZ_SUBTITLE.slice(1)}.
              </p>

              <div className="mt-10 flex items-center justify-center gap-8 text-sm text-zinc-300">
                <span className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-[#8b5cf6]" /> 3 domande
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#8b5cf6]" /> 40 secondi
                </span>
              </div>

              <motion.button
                onClick={() => {
                  setPhase("flow");
                  setStepIndex(0);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                data-testid="quiz-begin-btn"
                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#6d28d9] hover:bg-[#8b5cf6] px-9 py-4 text-base font-semibold text-white transition-colors"
              >
                Inizia il questionario
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 group-hover:rotate-45 transition-transform">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </motion.button>
            </motion.div>
          )}

          {phase === "flow" && current.type === "leadform" && (
            <LeadForm key="leadform" initial={form} onContinue={handleFormContinue} />
          )}

          {phase === "flow" && current.type === "question" && (
            <QuestionCard
              key={`q-${current.q}`}
              question={QUESTIONS[current.q]}
              total={3}
              onSelect={handleSelect}
            />
          )}

          {phase === "flow" && current.type === "interstitial" && (
            <Interstitial
              key={`int-${current.pair}`}
              cases={FEEDBACK_PAIRS[current.pair]}
              onContinue={advance}
            />
          )}

          {phase === "flow" && current.type === "result" && (
            <OfferResult
              key="result"
              tier={tier}
              onContinue={() => setPhase("thankyou")}
            />
          )}

          {phase === "thankyou" && (
            <ThankYou
              key="thankyou"
              name={form?.name}
              onExit={handleReset}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
